import type { CartItem } from '../types/cartTypes';

const STOREFRONT_ACCESS_TOKEN = '609884cb58766ce2eb472fa05b47d7f0';
const GRAPHQL_ENDPOINT = 'https://d1241f-6b.myshopify.com/api/2023-10/graphql.json';

/**
 * Storefront API 优惠验证结果
 */
export interface StorefrontDiscountResult {
  /** 优惠码是否有效 */
  isValid: boolean;
  /** 优惠金额 (分) */
  discountAmount: number;
  /** 应用优惠后的总价 (分) */
  totalPrice: number;
  /** 货币代码 */
  currency: string;
  /** 错误信息 */
  error?: string;
}


/**
 * 将数字 ID 转换为 Shopify Storefront API 需要的 Global ID
 */
function toGlobalId(type: 'ProductVariant', id: number): string {
  return btoa(`gid://shopify/${type}/${id}`);
}

/**
 * Shopify Storefront API 客户端，目前主要用于在购物车中异步验证折扣码。
 */
export const storefrontApi = {

  /**
   * 创建一个“影子购物车”来验证折扣码
   */
  validateDiscount: async (items: CartItem[], discountCode: string): Promise<StorefrontDiscountResult> => {
    const lines = items.map(item => ({
      merchandiseId: toGlobalId('ProductVariant', item.id),
      quantity: item.quantity
    }));

    const query = `
      mutation validateDiscount($cartInput: CartInput!) {
        cartCreate(input: $cartInput) {
          cart {
            id
            discountCodes {
              code
              applicable
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
              }
            }
            discountAllocations {
              discountedAmount {
                amount
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      cartInput: {
        lines: lines,
        discountCodes: [discountCode]
      }
    };

    try {
      const response = await fetch(GRAPHQL_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
        },
        body: JSON.stringify({ query, variables })
      });

      const json = await response.json();

      if (json.errors) {
        console.error('Storefront API GraphQL Errors:', json.errors);
        return { isValid: false, discountAmount: 0, totalPrice: 0, currency: '', error: 'API Error' };
      }

      const result = json.data?.cartCreate;

      if (!result) {
        return { isValid: false, discountAmount: 0, totalPrice: 0, currency: '', error: 'Unknown Error' };
      }

      if (result.userErrors && result.userErrors.length > 0) {
        return {
          isValid: false,
          discountAmount: 0,
          totalPrice: 0,
          currency: '',
          error: result.userErrors[0].message
        };
      }

      const cart = result.cart;
      const discountInfo = cart.discountCodes?.[0];

      if (!discountInfo?.applicable) {
        return {
          isValid: false,
          discountAmount: 0,
          totalPrice: Math.round(parseFloat(cart.cost.totalAmount.amount) * 100),
          currency: cart.cost.totalAmount.currencyCode,
          error: 'Enter a valid discount code' // Shopify 有时返回 applicable: false 但没 userErrors
        };
      }

      // 计算总折扣金额
      let totalDiscount = 0;
      if (cart.discountAllocations) {
        totalDiscount = cart.discountAllocations.reduce((sum: number, alloc: any) => {
          return sum + parseFloat(alloc.discountedAmount.amount);
        }, 0);
      }

      return {
        isValid: true,
        discountAmount: Math.round(totalDiscount * 100), // 转为 cents
        totalPrice: Math.round(parseFloat(cart.cost.totalAmount.amount) * 100),
        currency: cart.cost.totalAmount.currencyCode
      };

    } catch (error) {
      console.error('Storefront API Network Error:', error);
      return {
        isValid: false,
        discountAmount: 0,
        totalPrice: 0,
        currency: '',
        error: 'Network Error'
      };
    }
  }
};
